import { Box, useMantineTheme, Text, ActionIcon, Menu, Flex, Badge, Avatar, Skeleton } from "@mantine/core"
import { useContext, useEffect, useRef, useState } from "react"
import { Playlist, PlaylistVideo, VideoEvent } from "../../client"
import classes from './VideoCard.module.css'
import React from "react"
import ReactPlayer from "react-player"
import { useInView } from 'react-intersection-observer';
import WrappedPlayer from "../WrappedPlayer/WrappedPlayer"
import { useAuth0 } from "@auth0/auth0-react"
import { IconDotsVertical, IconPlaylistAdd, IconTrash } from "@tabler/icons-react"
import { useLocation, useNavigate } from "react-router-dom"
import dayjs from "dayjs"
import { deleteVideoFromPlaylist } from "../../api/Playlists"
import { NBAInsightsContext } from "../../context/global-state"
import PlayerAvatar from "../Common/PlayerAvatar"
import EventTagBadges from "../Common/EventTagBadges"
import { getPlayerHeadshotUrl, getEventPlayers, isValidPlayer, resolveTargetPlayerId } from "../../utils/player"
import { useHideOutcomes } from "../../hooks/useHideOutcomes"
import { getVideoLeague } from "../../utils/league"

interface VideoCardProps {
  action: "add" | "delete" | "view"
  video: VideoEvent,
  onSelect: (video: VideoEvent) => void,
  targetPlayerId?: string | null,
  targetPlayerName?: string | null,
}

declare global {
  interface Document {
    webkitFullscreenElement?: Element | null
  }
}

const isDocumentInFullscreen = (): boolean => {
  return Boolean(document.fullscreenElement || document.webkitFullscreenElement)
}

const VideoCard: React.FC<VideoCardProps> = ({action, video, onSelect, targetPlayerId, targetPlayerName}) => {
  const clipLeague = getVideoLeague(video)
  const [hideOutcomes] = useHideOutcomes()
  const { setUserPlaylists } = useContext(NBAInsightsContext)
  const navigate = useNavigate()
  const location = useLocation()
  const searchParams = new URLSearchParams(location.search)
  const activeTargetPlayerId =
    targetPlayerId ||
    searchParams.get('player') ||
    resolveTargetPlayerId(video, searchParams.get('players')?.split(','), searchParams.get('stats')?.split(','))
  const { primary, secondary } = getEventPlayers(video, activeTargetPlayerId, targetPlayerName)
  const hasSecondary = Boolean(secondary && isValidPlayer(secondary.id, secondary.name))
  const { isAuthenticated, getAccessTokenSilently } = useAuth0()
  const { ref, inView } = useInView({
    triggerOnce: true,
    rootMargin: "150px"
  });

  const [playing, setPlaying] = useState<boolean>(false)

  const theme = useMantineTheme()
  const videoRef = useRef<ReactPlayer | null>(null)
  const hoverTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const isHoveredRef = useRef<boolean>(false)

  const handleMouseEnter = () => {
    isHoveredRef.current = true
    if (hoverTimerRef.current) {
      clearTimeout(hoverTimerRef.current)
    }
    hoverTimerRef.current = setTimeout(() => {
      setPlaying(true)
    }, 400)
  }

  const handleMouseLeave = () => {
    isHoveredRef.current = false
    if (hoverTimerRef.current) {
      clearTimeout(hoverTimerRef.current)
      hoverTimerRef.current = null
    }
    if (isDocumentInFullscreen()) {
      return
    }
    setPlaying(false)
  }

  useEffect(() => {
    const handleFullscreenChange = () => {
      if (!isDocumentInFullscreen() && !isHoveredRef.current) {
        setPlaying(false)
      }
    }

    document.addEventListener("fullscreenchange", handleFullscreenChange)
    document.addEventListener("webkitfullscreenchange", handleFullscreenChange)
    return () => {
      document.removeEventListener("fullscreenchange", handleFullscreenChange)
      document.removeEventListener("webkitfullscreenchange", handleFullscreenChange)
      if (hoverTimerRef.current) {
        clearTimeout(hoverTimerRef.current)
      }
    }
  }, [])
  const handleOnClick = () => {
    onSelect(video)
  }

  const getDuration = (): number | null => {
    try {
      const duration = videoRef.current?.getDuration()
      return duration ? Math.floor(duration) : null
    } catch {
      return null
    }
  }

  const handleDelete = async (video: PlaylistVideo) => {
    const token = await getAccessTokenSilently()
    await deleteVideoFromPlaylist(video.id, video.playlistId, token)
    setUserPlaylists((prev: Playlist[]) => prev.map(playlist => 
    playlist.id === video.playlistId 
      ? { ...playlist, videos: playlist.videos.filter(v => v.id !== video.id) }
      : playlist
    ))
  }

  const matchupScoreText = (() => {
    const homeAbbv = (video.hAbbv || '').trim();
    const awayAbbv = (video.vAbbv || '').trim();

    const hasScore = Boolean(video.score && video.score !== 'None' && video.score.includes('-'));
    const parts = hasScore ? video.score!.split('-').map(s => s.trim()) : [];
    const validScores = parts.length === 2 && parts[0] !== '' && parts[1] !== '';

    if (homeAbbv && awayAbbv) {
      if (validScores) {
        return `${awayAbbv} ${parts[0]} @ ${homeAbbv} ${parts[1]}`;
      }
      return `${awayAbbv} @ ${homeAbbv}`;
    }

    if (validScores) {
      return `Away ${parts[0]} @ Home ${parts[1]}`;
    }

    if (video.score && video.score !== 'None') {
      return video.score;
    }

    return null;
  })();

  return (
    <Box
      ref={ref}
      style={{
        borderRadius: 20,
        padding: 12,
        margin: 4,
        background: playing ? theme.colors.dark[6] : theme.colors.dark[8],
        border: `1px solid ${playing ? theme.colors.dark[4] : theme.colors.dark[7]}`,
        transition: "background-color 150ms ease, border-color 150ms ease",
        height: "calc(100% - 8px)",
      }}
      className={classes.box}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <Box
        onClick={handleOnClick}
      >
        <Flex ml={5} mr={5} mb={6} justify="space-between" align="center">
          <Text fz="xs" c="dimmed" fw={500}>
            {video.date ? dayjs(video.date).format('MMM D, YYYY') : ''}
          </Text>
          <Flex align="center" gap={6}>
            {matchupScoreText && (
              <Badge
                size="sm"
                variant="default"
                radius="sm"
                className={`${classes.outcomeContent} ${hideOutcomes ? classes.outcomeBlur : ''}`}
              >
                {matchupScoreText}
              </Badge>
            )}
            <Badge size="sm" variant="default" radius="sm">
              {video.period < 5 ? `Q${video.period}` : `OT${video.period}`} {video.clockTime}
            </Badge>
          </Flex>
        </Flex>
        <WrappedPlayer
          video={video}
          inView={inView}
          reactPlayerProps={{
            style: {
              position: "absolute",
              top: "0",
              left: "0",
              borderRadius:" 20px",
              overflow: "hidden",
            },
            ref: videoRef,
            playing,
            controls: playing,
            muted: true,
            width: "100%",
            height: "100%",
          }}
        />
          <Flex direction={"column"}>
            <Flex m={5}>
              <Flex flex={9} align={"center"}>
                {hasSecondary ? (
                  <Avatar.Group mr={10} style={{ justifySelf: "flex-start" }}>
                    <PlayerAvatar
                      playerId={primary.id}
                      playerName={primary.name}
                      league={clipLeague}
                      size={35}
                    />
                    <PlayerAvatar
                      playerId={secondary!.id}
                      playerName={secondary!.name}
                      league={clipLeague}
                      size={35}
                    />
                  </Avatar.Group>
                ) : (
                  <PlayerAvatar
                    playerId={primary.id}
                    playerName={primary.name}
                    league={clipLeague}
                    size={35}
                    style={{ justifySelf: "flex-start" }}
                    mr={10}
                  />
                )}
                <Flex direction={"column"} style={{ overflow: "hidden" }}>
                  <Text fw={600} fz={"md"} truncate>{ primary.name }</Text>
                  {hasSecondary && secondary?.name ? (
                    <Text fw={500} fz={"xs"} c="dimmed" truncate>w/ { secondary.name }</Text>
                  ) : (
                    <Text fw={500} fz={"xs"} style={{ visibility: "hidden" }} aria-hidden="true">&nbsp;</Text>
                  )}
                </Flex>
              </Flex>
              { action !== "view"  && isAuthenticated && (
                <Flex flex={1} m={5} mr={0} mb={0} align={"center"} justify={"center"}>
                  <Menu>
                    <Menu.Target>
                      <ActionIcon variant="transparent" c={"white"} onClick={(e) => e.stopPropagation()}>
                        <IconDotsVertical size={20}/>
                      </ActionIcon>
                    </Menu.Target>
                    <Menu.Dropdown>
                      { action === "add" && (
                          <Menu.Item leftSection={<IconPlaylistAdd size={20}/>} onClick={(e) => {
                            e.stopPropagation()
                            navigate("/modals/add-to-playlist", { state: { previousLocation: location, playlistVideoParams: {
                              videoThumbnail: getPlayerHeadshotUrl(primary.id, clipLeague) || '',
                              videoDuration: getDuration(),
                              ...video,
                            } }})
                          }}>
                            <Menu.Label c={"white"}>Add to playlist</Menu.Label>
                          </Menu.Item>
                        )
                      }

                    { action === "delete" && (
                        <Menu.Item leftSection={<IconTrash size={20}/>} onClick={(e) => {
                          e.stopPropagation()
                          handleDelete(video as PlaylistVideo)
                        }}>
                          <Menu.Label c={"white"}>Remove</Menu.Label>
                        </Menu.Item>
                      )
                    }
                    </Menu.Dropdown>
                  </Menu>
                </Flex>
              )}
            </Flex>
            <EventTagBadges
              video={video}
              targetPlayerId={activeTargetPlayerId}
              targetPlayerName={targetPlayerName}
              className={`${classes.outcomeContent} ${hideOutcomes ? classes.outcomeBlur : ''}`}
            />
          </Flex>
        </Box>
    </Box>
  )
}

export const VideoCardSkeleton: React.FC = () => {
  const theme = useMantineTheme()

  return (
    <Box
      style={{
        borderRadius: 20,
        padding: 12,
        margin: 4,
        background: theme.colors.dark[8],
        border: `1px solid ${theme.colors.dark[7]}`,
        height: "calc(100% - 8px)",
      }}
    >
      <Flex ml={5} mr={5} mb={6} justify="space-between" align="center">
        <Skeleton height={14} width={75} radius="sm" animate />
        <Skeleton height={20} width={65} radius="sm" animate />
      </Flex>
      <Box style={{ position: "relative", width: "100%", aspectRatio: "16 / 9", borderRadius: 20, overflow: "hidden" }}>
        <Skeleton height="100%" width="100%" radius={20} animate />
      </Box>
      <Flex m={5} align="center">
        <Skeleton height={35} width={35} radius="xl" mr={10} animate />
        <Flex direction="column" gap={4} style={{ flex: 1, overflow: "hidden" }}>
          <Skeleton height={16} width="65%" radius="sm" animate />
          <Skeleton height={12} width="45%" radius="sm" animate />
        </Flex>
      </Flex>
      <Flex m={5} ml={10} gap={6} style={{ minHeight: 24 }} align="center">
        <Skeleton height={18} width={50} radius="sm" animate />
        <Skeleton height={18} width={60} radius="sm" animate />
      </Flex>
    </Box>
  )
}

export default VideoCard